import React from "react";
import type { FormControlProps } from "@mui/joy";
import { FormControl, FormLabel, Option } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import DarkSelect from "./DarkSelect";
import { ctaAccentColor } from "../../styles/globalStyles";

/** HH:mm slots every 30 minutes (same grid as excursion form). */
export const DARK_TIME_SLOTS: readonly string[] = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
})();

const slotsList = DARK_TIME_SLOTS as string[];

export function darkTimeStartOptions(value: string): string[] {
  if (!value || slotsList.includes(value)) {
    return [...slotsList];
  }
  return [value, ...slotsList];
}

export function darkTimeEndOptions(startTime: string, currentEndTime: string): string[] {
  const afterStart = slotsList.filter((t) => t > startTime);
  if (!currentEndTime || afterStart.includes(currentEndTime)) {
    return afterStart;
  }
  return [currentEndTime, ...afterStart.filter((t) => t > currentEndTime)];
}

export function nextDarkTimeSlotAfter(t: string): string {
  const idx = slotsList.indexOf(t);
  if (idx < 0 || idx >= slotsList.length - 1) {
    return slotsList[slotsList.length - 1];
  }
  return slotsList[idx + 1];
}

export type DarkTimePickerProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Start: full slot list; end: only slots strictly after `startTime`. */
  mode: "start" | "end";
  /** Used when mode is "end". */
  startTime?: string;
  /** When set, wraps the select in FormControl + FormLabel. */
  label?: React.ReactNode;
  labelAccent?: boolean;
  labelSize?: "default" | "compact";
  formControlProps?: Omit<FormControlProps, "children" | "required">;
  required?: boolean;
  selectSx?: SxProps;
};

export default function DarkTimePicker({
  value,
  onChange,
  disabled,
  mode,
  startTime = "",
  label,
  labelAccent = false,
  labelSize = "default",
  required = false,
  formControlProps,
  selectSx,
}: DarkTimePickerProps) {
  const options =
    mode === "start" ? darkTimeStartOptions(value) : darkTimeEndOptions(startTime, value);

  const selectValue =
    mode === "start"
      ? value || DARK_TIME_SLOTS[0]
      : value && options.includes(value)
        ? value
        : (options[0] ?? DARK_TIME_SLOTS[0]);

  React.useEffect(() => {
    if (mode !== "end" || !startTime) {
      return;
    }
    if (value && value <= startTime) {
      onChange(nextDarkTimeSlotAfter(startTime));
    }
  }, [mode, startTime, value, onChange]);

  const select = (
    <DarkSelect
      value={selectValue}
      onChange={(_, v) => v != null && onChange(v)}
      disabled={disabled}
      sx={{ minWidth: 0, ...selectSx }}
    >
      {options.map((t) => (
        <Option key={t} value={t}>
          {t}
        </Option>
      ))}
    </DarkSelect>
  );

  if (label == null) {
    return select;
  }

  const labelSx = labelAccent
    ? { color: ctaAccentColor, fontSize: { xs: "0.8rem", sm: "0.875rem" } as const }
    : {
        fontSize:
          labelSize === "compact"
            ? ({ xs: "0.7rem", sm: "0.875rem" } as const)
            : ({ xs: "0.8rem", sm: "0.875rem" } as const),
      };

  return (
    <FormControl required={required} {...formControlProps}>
      <FormLabel sx={labelSx}>{label}</FormLabel>
      {select}
    </FormControl>
  );
}
